import CheckMarkIcon from '../../../assets/icons/checkmark.svg?react';
import { ChangeEvent } from 'react';
import Tooltip from '../../common/tooltip.tsx';
import Markdown from '../../markdown.tsx';
import { cn, orderToLetter } from '../../../utils/common-utils.ts';
import HighlightedText from '../../hightlight/index.tsx';
import { HighlightContentTypeEnum } from '../../../enums/index.ts';

interface IProps {
  text: string;
  value: number;
  order: number;
  isChecked: boolean;
  isCrossOutMode: boolean;
  onSetExcludedChoices?: (isCrossed: boolean, choiceId: number) => void;
  onChange?: (value?: string | number) => void;
  isRightAnswerMark?: boolean;
  isWrongAnswerMark?: boolean;
  isDisabled?: boolean;
  excluded?: boolean;
  isPreviewMode?: boolean;
  isShowHighLights?: boolean;
  questionId?: number;
}

const QuestionAnswer = ({
  text,
  order,
  value,
  isChecked,
  isCrossOutMode,
  onChange,
  isDisabled,
  excluded,
  onSetExcludedChoices,
  isRightAnswerMark = false,
  isWrongAnswerMark = false,
  isPreviewMode = false,
  isShowHighLights,
  questionId,
}: IProps) => {
  const toggleCross = () => {
    onSetExcludedChoices?.(!excluded, value);
    if (onChange && isChecked) {
      onChange(undefined);
    }
  };

  const changeAnswer = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onSetExcludedChoices?.(false, value);
      onChange(Number(target.value));
    }
  };

  const id = 'answer-' + value;

  return (
    <div className={'flex items-start font-gentium text-base'}>
      <label
        htmlFor={id}
        className={cn(
          'relative flex w-full cursor-pointer flex-nowrap items-start justify-between rounded-[1.1rem] border border-black py-3',
          isChecked && !isPreviewMode && 'border-blue text-blue',
          excluded && isCrossOutMode && 'text-[#B1B9C6]',
          isCrossOutMode && 'mr-3',
          isWrongAnswerMark && 'border-red bg-light-red',
        )}
      >
        {excluded && isCrossOutMode && (
          <div
            className={
              'absolute top-1/2 -ml-[1%] h-0.5 w-[102%] -translate-y-1/2 bg-black'
            }
          />
        )}
        <div className={'flex w-full items-start px-3'}>
          <span
            className={cn(
              'flex-center mt-px h-8 w-8 min-w-8 select-none rounded-full border-[2px] border-[#5B5B5B] text-[1rem] font-semibold text-[#4B5563]',
              isChecked && 'border-blue bg-blue text-white',
              excluded && isCrossOutMode && 'text-[#B1B9C6]',
            )}
          >
            {orderToLetter(order)}
          </span>
          <div
            className={
              'ml-4 flex-1 pt-[5px] leading-[1.32] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_div]:my-0 [&_li]:my-0 [&_ol]:my-0 [&_p]:m-0 [&_ul]:my-0'
            }
          >
            {isShowHighLights ? (
              <HighlightedText
                key={`${questionId}_${HighlightContentTypeEnum.CHOICE}_${order}`}
                value={text}
                questionId={questionId!}
                highlightParentKey={`${questionId}_${HighlightContentTypeEnum.CHOICE}_${order}`}
              />
            ) : (
              <Markdown>{text}</Markdown>
            )}
          </div>
        </div>
      </label>
      <input
        className={'hidden'}
        name={'answers-group'}
        type={'radio'}
        id={id}
        value={value}
        checked={isChecked}
        disabled={isDisabled}
        onChange={changeAnswer}
      />
      {isCrossOutMode && (
        <div className={'flex w-7 items-center justify-center self-stretch'}>
          <Tooltip tip={excluded ? 'Remove mark' : 'Mark as incorrect'}>
            <button
              onClick={toggleCross}
              className={cn(
                'flex-center relative h-6 cursor-pointer select-none text-[0.7rem] font-bold',
                !excluded
                  ? 'w-6 rounded-full border border-black'
                  : 'min-w-[1.8rem] font-sans text-black underline',
              )}
            >
              {!excluded && (
                <div
                  className={
                    'absolute top-1/2 h-0.5 w-6 -translate-y-1/2 bg-black'
                  }
                />
              )}
              {excluded ? 'Undo' : orderToLetter(order)}
            </button>
          </Tooltip>
        </div>
      )}
      {isPreviewMode && (
        <div className="ml-2 flex w-10 items-center justify-center self-stretch">
          {isRightAnswerMark && (
            <CheckMarkIcon className="h-6 w-6" fill={'#43A95F'} />
          )}
          {isWrongAnswerMark && (
            <CheckMarkIcon className="h-6 w-6" fill={'#921818'} />
          )}
        </div>
      )}
    </div>
  );
};
export default QuestionAnswer;
